import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { WarehousingMasterType, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData, WarehousingTrackingPreview, WarehousingTrackingResult } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-item-view',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, BreadcrumbModule, ButtonModule, ChipModule, DialogModule, ImageModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, TextareaModule, ProgressBarModule, TagModule, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent],
  templateUrl: './warehousing-item-view.component.html'
})
export class WarehousingItemViewComponent implements OnInit {
  readonly itemSizeLabel = itemSizeLabel;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private permissionService = inject(PermissionService);

  readonly canEdit = this.permissionService.hasPermission(app_permissions.warehousing.items.edit);
  trackingDialogVisible = false;
  trackingRepair = false;
  trackingBusy = false;
  trackingReason = '';
  trackingError = '';
  trackingPreview?: WarehousingTrackingPreview;
  trackingResult?: WarehousingTrackingResult;

  isLoading = false;
  item?: WarehousingItem;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Item Details', 'Items', '/warehousing/items');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.loadItem();
  }

  loadItem(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      items: this.warehousingService.getItems(),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      categories: this.warehousingService.getMasterData('CATEGORY'),
      brands: this.warehousingService.getMasterData('BRAND'),
      subBrands: this.warehousingService.getMasterData('SUB_BRAND'),
      tenantUoms: this.warehousingService.getMasterData('TENANT_UOM')
    }).subscribe({
      next: response => {
        this.item = (response.items.data || []).find(row => row.id === id);
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.CATEGORY = response.categories.data || [];
        this.masterData.BRAND = response.brands.data || [];
        this.masterData.SUB_BRAND = response.subBrands.data || [];
        this.masterData.TENANT_UOM = response.tenantUoms.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.item
          ? [this.item.itemNumber || 'Item Details', itemSizeLabel(this.item)].filter(Boolean).join(' | ')
          : 'Item Details', 'Items', '/warehousing/items');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  editItem(): void {
    if (this.item?.id) this.router.navigate(['/warehousing/items/edit', this.item.id]);
  }

  previewTrackingChange(): void {
    if (!this.item?.id) return;
    const toMode = this.item.trackingMode === 'UNIT' ? 'QUANTITY' : 'UNIT';
    this.trackingBusy = true;
    this.trackingRepair = false;
    this.trackingError = '';
    this.trackingPreview = undefined;
    this.trackingResult = undefined;
    this.trackingReason = '';
    this.warehousingService.previewTrackingChange(this.item.id, toMode).subscribe({
      next: response => {
        this.trackingPreview = response.data;
        this.trackingDialogVisible = true;
        this.trackingBusy = false;
      },
      error: error => {
        this.trackingError = error?.error?.message || 'Unable to preview the tracking change.';
        this.trackingDialogVisible = true;
        this.trackingBusy = false;
      }
    });
  }

  previewTrackingRepair(): void {
    if (!this.item?.id) return;
    this.trackingBusy = true;
    this.trackingRepair = true;
    this.trackingError = '';
    this.trackingPreview = undefined;
    this.trackingResult = undefined;
    this.trackingReason = '';
    this.warehousingService.previewTrackingRepair(this.item.id).subscribe({
      next: response => {
        this.trackingPreview = response.data;
        this.trackingDialogVisible = true;
        this.trackingBusy = false;
      },
      error: error => {
        this.trackingError = error?.error?.message || 'Unable to preview the pooled stock repair.';
        this.trackingDialogVisible = true;
        this.trackingBusy = false;
      }
    });
  }

  confirmTrackingChange(): void {
    if (!this.item?.id || !this.trackingPreview || !this.trackingReason.trim()) return;
    this.trackingBusy = true;
    this.trackingError = '';
    const request = this.trackingRepair
      ? this.warehousingService.confirmTrackingRepair(this.item.id, this.trackingPreview, this.trackingReason.trim())
      : this.warehousingService.confirmTrackingChange(this.item.id, this.trackingPreview, this.trackingReason.trim());
    request.subscribe({
      next: response => {
        this.trackingResult = response.data;
        this.trackingBusy = false;
        if (this.trackingResult?.status === 'COMPLETED') this.loadItem();
      },
      error: error => {
        this.trackingError = error?.error?.message || 'Tracking change was not confirmed. Create a fresh preview.';
        this.trackingBusy = false;
      }
    });
  }

  metrics(): PageMetric[] {
    return [
      { label: 'SQM', value: this.formatNumber(this.item?.sqm, 4), tone: 'info' },
      { label: 'CBM', value: this.formatNumber(this.item?.cbm, 4), tone: 'info' },
      { label: 'Weight', value: `${this.formatNumber(this.item?.weightKg, 4)} kg` },
      { label: 'Tracking', value: this.item?.trackingMode === 'UNIT' || this.item?.isDisplayStand ? 'UNIT-LEVEL TRACKING REQUIRED' : this.item?.trackingMode || '-' }
    ];
  }

  dimensionUnitName(): string {
    return this.item?.lengthUnitName || 'm';
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  accountName(id?: number): string {
    return this.accounts.find(account => account.id === id)?.companyName || '-';
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  hierarchyLabel(): string {
    if (!this.item) return '-';
    return this.item.hierarchyPath || [
      this.item.categoryName || this.masterName('CATEGORY', this.item.categoryId),
      this.item.brandName || this.masterName('BRAND', this.item.brandId),
      this.item.subBrandName || this.masterName('SUB_BRAND', this.item.subBrandId)
    ].filter(value => value && value !== '-').join(' > ') || '-';
  }

  statusSeverity = statusSeverity;

  private formatNumber(value?: number, maximumFractionDigits = 2): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value || 0);
  }
}
